// Evidence line status (spec J5, §5.5): PLANNED → IMPLEMENTED → VERIFIED, GAP (EXC) by the owner.
import { hasPermission, type Role } from './roles.ts';
import { isExceptionId } from './leads.ts';
import type { RefusalKind } from './task-state.ts';

export const EVIDENCE_STATUSES = ['planned', 'implemented', 'verified', 'gap_exc'] as const;
export type EvidenceStatus = (typeof EVIDENCE_STATUSES)[number];

// add-record is applied by the records endpoint; the other three are POST .../actions/:action.
export const EVIDENCE_ACTIONS = ['add-record', 'check', 'return', 'set-gap'] as const;
export type EvidenceAction = (typeof EVIDENCE_ACTIONS)[number];

export interface EvidenceContext {
  readonly role: Role;
  // At least one live record on the line carries a SHA-256 (required for VERIFIED).
  readonly hasHashedRecord?: boolean;
  readonly reason?: string;
  readonly exceptionId?: string;
}

export type EvidenceRefusalCode =
  'invalid_transition' | 'not_allowed' | 'reason_required' | 'hash_required' | 'exception_required';

export type EvidenceResult =
  | { readonly ok: true; readonly to: EvidenceStatus }
  | { readonly ok: false; readonly code: EvidenceRefusalCode; readonly kind: RefusalKind };

type Guard = (ctx: EvidenceContext, from: EvidenceStatus) => EvidenceResult | null;

const refuse = (code: EvidenceRefusalCode, kind: RefusalKind): EvidenceResult => ({
  ok: false,
  code,
  kind,
});

const needs = (ok: boolean, code: EvidenceRefusalCode, kind: RefusalKind): EvidenceResult | null =>
  ok ? null : refuse(code, kind);

interface EvidenceRule {
  readonly from: readonly EvidenceStatus[];
  // null keeps the current status (a new record on an implemented line stays implemented).
  readonly to: EvidenceStatus | null;
  readonly guards: readonly Guard[];
}

const hasReason: Guard = (ctx) =>
  needs((ctx.reason ?? '').trim() !== '', 'reason_required', 'bad_request');

// Only the owner may move a line out of GAP (EXC); checkers may return implemented or verified lines.
const mayReturn: Guard = (ctx, from) =>
  needs(from !== 'gap_exc' || ctx.role === 'owner', 'not_allowed', 'forbidden');

export const EVIDENCE_RULES: Readonly<Record<EvidenceAction, EvidenceRule>> = {
  'add-record': {
    from: ['planned', 'implemented', 'verified', 'gap_exc'],
    to: null,
    guards: [(ctx) => needs(hasPermission(ctx.role, 'evidence.write'), 'not_allowed', 'forbidden')],
  },
  check: {
    from: ['implemented'],
    to: 'verified',
    guards: [
      (ctx) => needs(hasPermission(ctx.role, 'evidence.check'), 'not_allowed', 'forbidden'),
      (ctx) => needs(ctx.hasHashedRecord === true, 'hash_required', 'unprocessable'),
    ],
  },
  return: {
    from: ['implemented', 'verified', 'gap_exc'],
    to: 'planned',
    guards: [
      (ctx) => needs(hasPermission(ctx.role, 'evidence.check'), 'not_allowed', 'forbidden'),
      mayReturn,
      hasReason,
    ],
  },
  'set-gap': {
    from: ['planned', 'implemented', 'verified'],
    to: 'gap_exc',
    guards: [
      (ctx) => needs(ctx.role === 'owner', 'not_allowed', 'forbidden'),
      (ctx) => needs(isExceptionId(ctx.exceptionId ?? ''), 'exception_required', 'unprocessable'),
    ],
  },
};

export function canEvidenceTransition(
  from: EvidenceStatus,
  action: EvidenceAction,
  ctx: EvidenceContext,
): EvidenceResult {
  const rule = EVIDENCE_RULES[action];
  if (!rule.from.includes(from)) return refuse('invalid_transition', 'conflict');
  const refusal = rule.guards.reduce<EvidenceResult | null>(
    (found, guard) => found ?? guard(ctx, from),
    null,
  );
  if (refusal !== null) return refusal;
  // The first record on a planned line makes it implemented; later records keep the status.
  const to = rule.to ?? (from === 'planned' ? 'implemented' : from);
  return { ok: true, to };
}

export function isEvidenceStatus(value: string): value is EvidenceStatus {
  return (EVIDENCE_STATUSES as readonly string[]).includes(value);
}
